/**
 * Every server interaction the portal performs, as TanStack Query hooks.
 *
 * They live in one file because the invalidation graph is the interesting part and
 * it is only reviewable when it is visible in one place: assigning a seat changes
 * the seat list, the member list *and* the workspace overview, and scattering those
 * `invalidateQueries` calls across feature folders is how a UI ends up showing a
 * seat count that is one action out of date.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { capture } from '@/shared/lib/analytics'
import {
  accountSchema,
  approveResultSchema,
  auditEventSchema,
  catalogSchema,
  deviceSchema,
  offlineLicenseSchema,
  cavsdbDownloadsSchema,
  cavsdbIssuedSchema,
  cavsdbLicenseSchema,
  cavsdbTokenSchema,
  domainViewSchema,
  machineTokenSchema,
  invitationSchema,
  memberSchema,
  overviewSchema,
  pendingActivationSchema,
  policiesViewSchema,
  policyGroupsSchema,
  seatSchema,
  subscriptionViewSchema,
  telemetryCatalogSchema,
  telemetryStatsSchema,
  ticketDetailSchema,
  ticketPageSchema,
  uploadGrantSchema,
  type Policies,
  type ReadPolicy,
  type Role,
  type TicketStatus,
  orgTokenSchema,
  siemViewSchema,
  preindexSettingsSchema,
  ssoConfigSchema,
  ssoDiscoverySchema,
} from '@/entities/types'
import { ApiError, downloadBlob, request, requestCsv, uploadSigned } from './client'
import { compressImage, type CompressedImage } from '@/shared/lib/images'

/** Query keys, centralised so an invalidation cannot typo its way into a no-op. */
export const keys = {
  account: ['account'] as const,
  subscription: (orgId?: string) => ['subscription', orgId ?? 'personal'] as const,
  devices: ['devices'] as const,
  machineTokens: ['machine-tokens'] as const,
  catalog: ['catalog'] as const,
  telemetryCatalog: ['telemetry-catalog'] as const,
  telemetryStats: ['telemetry-stats'] as const,
  org: (id: string) => ['org', id] as const,
  members: (id: string) => ['org', id, 'members'] as const,
  seats: (id: string) => ['org', id, 'seats'] as const,
  orgDevices: (id: string) => ['org', id, 'devices'] as const,
  offlineLicenses: (id: string) => ['org', id, 'offline-licenses'] as const,
  cavsdbLicenses: (id: string) => ['org', id, 'cavsdb', 'licenses'] as const,
  cavsdbDownloads: (id: string, channel: string) => ['org', id, 'cavsdb', 'downloads', channel] as const,
  policies: (id: string) => ['org', id, 'policies'] as const,
  policyGroups: (id: string) => ['org', id, 'policies', 'groups'] as const,
  usage: (id: string) => ['org', id, 'usage'] as const,
  domain: (id: string) => ['org', id, 'domain'] as const,
  sso: (id: string) => ['org', id, 'sso'] as const,
  orgTokens: (id: string) => ['org', id, 'tokens'] as const,
  siem: (id: string) => ['org', id, 'siem'] as const,
  preindexCfg: (id: string) => ['org', id, 'preindex'] as const,
  audit: (id: string, cursor: string) => ['org', id, 'audit', cursor] as const,
  activation: (code: string) => ['activation', code] as const,
  tickets: (cursor: string) => ['support', 'tickets', cursor] as const,
  ticket: (id: string) => ['support', 'ticket', id] as const,
}

// --- public ------------------------------------------------------------------

export function useCatalog() {
  return useQuery({
    queryKey: keys.catalog,
    queryFn: () => request('/v1/plans', { schema: catalogSchema, anonymous: true }),
    // The price list changes when we change it, not while someone is reading it.
    staleTime: 30 * 60 * 1000,
  })
}

export function useTelemetryCatalog() {
  return useQuery({
    queryKey: keys.telemetryCatalog,
    queryFn: () => request('/v1/telemetry/catalog', { schema: telemetryCatalogSchema, anonymous: true }),
    staleTime: 30 * 60 * 1000,
  })
}

/**
 * The public figures: installations, repositories indexed, tokens saved.
 *
 * `retry: false` and no error surface. This feeds a band on the landing page, and
 * a marketing page that shows a spinner or an error where a number should be is
 * worse than one that shows nothing at all — the caller renders it only once the
 * data is there.
 *
 * The band stays live on a page left open: the query refetches on the same
 * cadence the server recomputes the aggregate, and not faster, because anything
 * faster returns the same numbers. Not while the tab is in the background — a
 * hidden page has nobody to show a new figure to.
 */
export function useTelemetryStats() {
  return useQuery({
    queryKey: keys.telemetryStats,
    queryFn: () => request('/v1/telemetry/stats', { schema: telemetryStatsSchema, anonymous: true }),
    staleTime: PUBLIC_STATS_REFRESH,
    refetchInterval: PUBLIC_STATS_REFRESH,
    refetchIntervalInBackground: false,
    retry: false,
  })
}

/**
 * How often the public aggregate moves. Mirrors `publicStatsTTL` in the API's
 * telemetry use case; change both or one of them is lying.
 */
const PUBLIC_STATS_REFRESH = 5 * 60 * 1000


// --- support -----------------------------------------------------------------

export function useTickets(cursor = '') {
  return useQuery({
    queryKey: keys.tickets(cursor),
    queryFn: () =>
      request(`/v1/support/tickets?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`, {
        schema: ticketPageSchema,
      }),
    // Keeps the previous page on screen while the next loads, rather than collapsing
    // the list to a spinner.
    placeholderData: (previous) => previous,
  })
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: keys.ticket(id ?? ''),
    queryFn: () => request(`/v1/support/tickets/${id}`, { schema: ticketDetailSchema }),
    enabled: Boolean(id),
    // The signed URLs on the attachments expire in minutes. Refetching on focus is
    // what keeps a tab left open overnight from showing broken images.
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  })
}

/** An attachment that has been compressed and uploaded, ready to be referenced. */
export interface PreparedAttachment {
  key: string
  filename: string
  width: number
  height: number
  sizeBytes: number
  originalBytes: number
  /** A local preview URL. The caller must revoke it when the form unmounts. */
  previewUrl: string
}

/**
 * Compresses one image and uploads it to the signed URL.
 *
 * Three steps that have to happen in this order and nowhere else: compress, ask for
 * a URL for the compressed size, PUT the bytes. Asking for the URL first would sign
 * a size the client has not produced yet, and the server checks the real object
 * afterwards — so a mismatch would cost the person their upload at submit time
 * rather than here, where it can be retried against one file.
 */
export function useUploadAttachment() {
  return useMutation({
    mutationFn: async (input: { file: File; maxBytes: number }): Promise<PreparedAttachment> => {
      const compressed: CompressedImage = await compressImage(input.file, input.maxBytes)
      if (compressed.blob.size > input.maxBytes) {
        throw new Error(
          `${input.file.name} is still too large after compression. Try cropping it to the part that matters.`,
        )
      }

      const grant = await request('/v1/support/uploads', {
        method: 'POST',
        body: {
          filename: compressed.filename,
          content_type: compressed.contentType,
          size_bytes: compressed.blob.size,
        },
        schema: uploadGrantSchema,
      })

      await uploadSigned(grant.url, grant.headers, compressed.blob)

      return {
        key: grant.key,
        filename: compressed.filename,
        width: compressed.width,
        height: compressed.height,
        sizeBytes: compressed.blob.size,
        originalBytes: compressed.originalBytes,
        previewUrl: URL.createObjectURL(compressed.blob),
      }
    },
  })
}

/** Maps prepared uploads onto the wire shape both create and comment take. */
function attachmentBody(attachments: PreparedAttachment[]) {
  return attachments.map((a) => ({
    key: a.key,
    filename: a.filename,
    width: a.width,
    height: a.height,
  }))
}

export function useCreateTicket() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { title: string; body: string; attachments: PreparedAttachment[] }) =>
      request('/v1/support/tickets', {
        method: 'POST',
        body: {
          title: input.title,
          body: input.body,
          attachments: attachmentBody(input.attachments),
          cli_version: '',
        },
        schema: ticketDetailSchema,
      }),
    onSuccess: (ticket) => {
      void client.invalidateQueries({ queryKey: ['support', 'tickets'] })
      // Seed the detail cache so navigating straight to the new ticket paints
      // immediately instead of showing a spinner for something we already have.
      client.setQueryData(keys.ticket(ticket.id), ticket)
    },
  })
}

export function useAddTicketComment(ticketId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { body: string; attachments: PreparedAttachment[] }) =>
      request(`/v1/support/tickets/${ticketId}/comments`, {
        method: 'POST',
        body: { body: input.body, attachments: attachmentBody(input.attachments) },
        schema: ticketDetailSchema,
      }),
    onSuccess: (ticket) => {
      client.setQueryData(keys.ticket(ticketId), ticket)
      // A comment can reopen a closed ticket, so the list's status column moved too.
      void client.invalidateQueries({ queryKey: ['support', 'tickets'] })
    },
  })
}

export function useSetTicketStatus(ticketId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (status: TicketStatus) =>
      request(`/v1/support/tickets/${ticketId}/status`, {
        method: 'PUT',
        body: { status },
        schema: ticketDetailSchema,
      }),
    onSuccess: (ticket) => {
      client.setQueryData(keys.ticket(ticketId), ticket)
      void client.invalidateQueries({ queryKey: ['support', 'tickets'] })
    },
  })
}

// --- account -----------------------------------------------------------------

export function useAccount(options?: Partial<UseQueryOptions<z.infer<typeof accountSchema>>>) {
  return useQuery({
    queryKey: keys.account,
    queryFn: () => request('/v1/me', { schema: accountSchema }),
    // The bootstrap call carries the entitlement, so it should not go stale for
    // long — but re-fetching on every window focus would hammer it.
    staleTime: 60 * 1000,
    ...options,
  })
}

export function useAcceptTerms() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => request('/v1/me/accept-terms', { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.account }),
  })
}

/**
 * The person deleting their own account. The API refuses (409) while a
 * subscription still renews or while they alone own a workspace other people
 * are in — guidance, not failure, and the dialog shows it as such. Nothing is
 * invalidated on success: the caller signs out, and sign-out clears the cache.
 */
export function useDeleteAccount() {
  return useMutation({
    mutationFn: (confirmEmail: string) =>
      request('/v1/me/delete', {
        method: 'POST',
        body: { confirm_email: confirmEmail },
      }),
  })
}


// --- devices -----------------------------------------------------------------

const devicesResponse = z.object({ devices: z.array(deviceSchema) })

export function useDevices() {
  return useQuery({
    queryKey: keys.devices,
    queryFn: () => request('/v1/account/devices', { schema: devicesResponse }),
    select: (data) => data.devices,
  })
}

export function useRevokeDevice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (deviceId: string) =>
      request(`/v1/account/devices/${deviceId}/revoke`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.devices })
      // A machine token's device carries the token with it when revoked.
      void client.invalidateQueries({ queryKey: keys.machineTokens })
    },
  })
}

/**
 * Takes a device off the list without deleting it. Reinstalling the CLI on that
 * machine brings the same row back rather than opening a second one, which is
 * why this is a status change and not a delete.
 */
export function useRemoveDevice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (deviceId: string) =>
      request(`/v1/account/devices/${deviceId}/remove`, { method: 'POST' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.devices })
      void client.invalidateQueries({ queryKey: keys.machineTokens })
    },
  })
}

/** Switches a device the account revoked itself back on. */
export function useReinstateDevice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (deviceId: string) =>
      request(`/v1/account/devices/${deviceId}/reinstate`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.devices }),
  })
}

export function useRenameDevice() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { deviceId: string; displayName: string }) =>
      request(`/v1/account/devices/${input.deviceId}`, {
        method: 'PATCH',
        body: { display_name: input.displayName },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.devices }),
  })
}

// --- machine tokens ------------------------------------------------------------

const machineTokensResponse = z.object({ machine_tokens: z.array(machineTokenSchema) })
const createdMachineTokenResponse = z.object({
  machine_token: machineTokenSchema,
  token: z.string(),
})

export function useMachineTokens() {
  return useQuery({
    queryKey: keys.machineTokens,
    queryFn: () => request('/v1/machine-tokens', { schema: machineTokensResponse }),
    select: (data) => data.machine_tokens,
  })
}

/**
 * Mints a machine token. The `token` in the response is the only time the secret
 * exists in the clear: it is stored hashed and no endpoint can show it again.
 *
 * `expiresInDays` of zero takes the deployment default; anything over the
 * ceiling (a year) is clamped rather than refused, so the picker never has to
 * know the ceiling.
 *
 * The device list is invalidated too, though nothing changes in it *yet*: the
 * token's device appears on its first lease, and a page that was open across
 * that moment should not have to be reloaded to see it.
 */
export function useCreateMachineToken() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; expiresInDays: number }) =>
      request('/v1/machine-tokens', {
        method: 'POST',
        body: { name: input.name, expires_in_days: input.expiresInDays },
        schema: createdMachineTokenResponse,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.machineTokens })
      void client.invalidateQueries({ queryKey: keys.devices })
    },
  })
}

/**
 * Ends a token and, on the server, the device it speaks through — so the
 * device list is invalidated with it. A container holding a lease keeps it
 * until the grace window closes; the next cold start is refused.
 */
export function useRevokeMachineToken() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (tokenId: string) => request(`/v1/machine-tokens/${tokenId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.machineTokens })
      void client.invalidateQueries({ queryKey: keys.devices })
    },
  })
}

// --- billing -----------------------------------------------------------------

export function useSubscription(orgId?: string) {
  return useQuery({
    queryKey: keys.subscription(orgId),
    queryFn: () =>
      request(`/v1/billing/subscription${orgId ? `?organization_id=${orgId}` : ''}`, {
        schema: subscriptionViewSchema,
      }),
  })
}

const urlResponse = z.object({ url: z.string() })

/**
 * Opens Stripe Checkout.
 *
 * The redirect is a full navigation rather than a new tab: Checkout is a payment
 * flow and a popup blocker silently eating it is worse than losing the portal tab,
 * which the return URL restores anyway.
 */
export function useCheckout() {
  return useMutation({
    mutationFn: (input: {
      plan: 'team'
      interval: 'monthly' | 'annual'
      seats?: number
      organizationId?: string
    }) =>
      request('/v1/billing/checkout-sessions', {
        method: 'POST',
        body: {
          plan: input.plan,
          interval: input.interval,
          seats: input.seats ?? 0,
          organization_id: input.organizationId ?? '',
        },
        schema: urlResponse,
      }),
    onSuccess: ({ url }, variables) => {
      capture('checkout_started', {
        plan: variables.plan,
        interval: variables.interval,
        seats: variables.seats ?? 0,
      })
      window.location.assign(url)
    },
  })
}

export function useBillingPortal() {
  return useMutation({
    mutationFn: (orgId?: string) =>
      request('/v1/billing/portal-sessions', {
        method: 'POST',
        body: { organization_id: orgId ?? '' },
        schema: urlResponse,
      }),
    onSuccess: ({ url }) => {
      capture('billing_portal_opened')
      window.location.assign(url)
    },
  })
}

/**
 * Submits an enterprise sales inquiry from the public pricing page.
 *
 * Anonymous on purpose: the person asking has no account yet. Success means the
 * lead is stored server-side — only then may the UI claim it was received.
 */
export function useSubmitEnterpriseLead() {
  return useMutation({
    mutationFn: (input: {
      name: string
      email: string
      company: string
      teamSize: string
      notes: string
      cfToken?: string
      website?: string
    }) =>
      request('/v1/enterprise/leads', {
        method: 'POST',
        anonymous: true,
        body: {
          name: input.name,
          email: input.email,
          company: input.company,
          team_size: input.teamSize,
          notes: input.notes,
          cf_token: input.cfToken,
          website: input.website,
        },
      }),
    onSuccess: () => {
      capture('enterprise_lead_submitted')
    },
  })
}

export function useSetSeatQuantity(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (quantity: number) =>
      request('/v1/billing/seats', {
        method: 'PUT',
        body: { organization_id: orgId, quantity },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.subscription(orgId) })
      void client.invalidateQueries({ queryKey: keys.seats(orgId) })
      void client.invalidateQueries({ queryKey: keys.org(orgId) })
    },
  })
}

// --- CLI activation ----------------------------------------------------------

export function usePendingActivation(userCode: string) {
  return useQuery({
    queryKey: keys.activation(userCode),
    queryFn: () =>
      request(`/v1/cli-auth/pending?user_code=${encodeURIComponent(userCode)}`, {
        schema: pendingActivationSchema,
      }),
    enabled: userCode.length > 0,
    retry: false,
    staleTime: Infinity,
  })
}

/** The loopback flow's lookup: the link carries a session id, so there is nothing to type. */
export function usePendingActivationById(sessionId: string) {
  return useQuery({
    queryKey: keys.activation(`session:${sessionId}`),
    queryFn: () =>
      request(`/v1/cli-auth/pending?session_id=${encodeURIComponent(sessionId)}`, {
        schema: pendingActivationSchema,
      }),
    enabled: sessionId.length > 0,
    // An activation code is one-time-use and expires in ten minutes; refetching it
    // would be pointless at best and confusing at worst.
    retry: false,
    staleTime: Infinity,
  })
}

export function useDenyActivation() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      request(`/v1/cli-auth/sessions/${sessionId}/deny`, {
        method: 'POST',
        body: {},
        schema: approveResultSchema,
      }),
  })
}

export function useApproveActivation() {
  const client = useQueryClient()
  return useMutation({
    // Approving a terminal is the approval and nothing else — no plan travels with
    // it, because there is nothing for one to start.
    mutationFn: (sessionId: string) =>
      request(`/v1/cli-auth/sessions/${sessionId}/approve`, {
        method: 'POST',
        body: {},
        schema: approveResultSchema,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.account })
    },
  })
}

// --- team --------------------------------------------------------------------

export function useOrganization(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.org(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}`, { schema: overviewSchema }),
    enabled: Boolean(orgId),
  })
}

const membersResponse = z.object({
  members: z.array(memberSchema),
  invitations: z.array(invitationSchema),
})

export function useMembers(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.members(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/members`, { schema: membersResponse }),
    enabled: Boolean(orgId),
  })
}

/**
 * Invalidates everything a membership or seat change can affect.
 *
 * These four always move together: a seat assignment changes the seat row, the
 * member's `has_seat`, the workspace's seat summary, and — because seats are billed
 * per quantity — the subscription's assigned count. Invalidating them as a set is
 * why the UI never shows a stale seat count.
 */
function invalidateWorkspace(client: ReturnType<typeof useQueryClient>, orgId: string) {
  void client.invalidateQueries({ queryKey: keys.members(orgId) })
  void client.invalidateQueries({ queryKey: keys.seats(orgId) })
  void client.invalidateQueries({ queryKey: keys.org(orgId) })
  void client.invalidateQueries({ queryKey: keys.subscription(orgId) })
}

export function useInvite(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { email: string; role: Role }) =>
      request(`/v1/organizations/${orgId}/invitations`, {
        method: 'POST',
        body: { email: input.email, role: input.role },
        schema: invitationSchema,
      }),
    onSuccess: () => invalidateWorkspace(client, orgId),
  })
}

export function useRevokeInvitation(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      request(`/v1/organizations/${orgId}/invitations/${invitationId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateWorkspace(client, orgId),
  })
}

export function useResendInvitation(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (invitationId: string) =>
      request(`/v1/organizations/${orgId}/invitations/${invitationId}/resend`, {
        method: 'POST',
        body: {},
        schema: invitationSchema,
      }),
    onSuccess: () => invalidateWorkspace(client, orgId),
  })
}

/**
 * Deletes the workspace. The backend refuses while the subscription is still set
 * to renew, so the error this surfaces is guidance ("cancel in the billing
 * portal first"), not failure.
 */
export function useDeleteWorkspace(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (confirmName: string) =>
      request(`/v1/organizations/${orgId}/delete`, {
        method: 'POST',
        body: { confirm_name: confirmName },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.account })
    },
  })
}

/** The organization usage & spend dashboard (Enterprise). */
export function useOrgUsage(orgId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: keys.usage(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/usage`, { schema: telemetryStatsSchema }),
    enabled: Boolean(orgId) && enabled,
    retry: false,
  })
}

/**
 * Creating a workspace. The one thing the portal had no way to do: the endpoint has
 * existed since Teams shipped and nothing called it, so the only route to a
 * workspace was a Stripe checkout — which a deployment with purchasing closed does
 * not have either.
 *
 * Invalidates the account, because the workspace it just created is what
 * `useCurrentOrgId` reads to find one.
 */
export function useCreateWorkspace() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      request(`/v1/organizations`, {
        method: 'POST',
        body: { name },
        schema: overviewSchema,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.account })
    },
  })
}

/** The workspace's domain claim, and the three things an admin does to it. */
export function useDomain(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.domain(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/domain`, { schema: domainViewSchema }),
    enabled: Boolean(orgId),
  })
}

export function useClaimDomain(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      request(`/v1/organizations/${orgId}/domain`, {
        method: 'PUT',
        body: { name },
        schema: domainViewSchema,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.domain(orgId) })
    },
  })
}

export function useVerifyDomain(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () =>
      request(`/v1/organizations/${orgId}/domain/verify`, {
        method: 'POST',
        schema: domainViewSchema,
      }),
    // Both ways: a failed check records what it found, which this screen renders.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.domain(orgId) })
    },
  })
}

export function useReleaseDomain(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => request(`/v1/organizations/${orgId}/domain`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.domain(orgId) })
      void client.invalidateQueries({ queryKey: keys.sso(orgId) })
    },
  })
}

export function useSSOConfig(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.sso(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/sso`, { schema: ssoConfigSchema }),
    enabled: Boolean(orgId),
  })
}

export function useSetSSO(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (config: { provider_id: string; domain: string; required: boolean; capture: boolean }) =>
      request(`/v1/organizations/${orgId}/sso`, { method: 'PUT', body: config }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.sso(orgId) })
    },
  })
}

/** SIEM audit streaming (Enterprise). The stored credential is reported as present, never echoed. */
export function useSIEMConfig(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.siem(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/siem`, { schema: siemViewSchema }),
    enabled: Boolean(orgId),
  })
}

export function useSetSIEM(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (config: { endpoint: string; authorization: string }) =>
      request(`/v1/organizations/${orgId}/siem`, { method: 'PUT', body: config }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.siem(orgId) })
    },
  })
}

/** Sends one synthetic event so a misconfigured collector fails here, not silently later. */
export function useTestSIEM(orgId: string) {
  return useMutation({
    mutationFn: () => request(`/v1/organizations/${orgId}/siem/test`, { method: 'POST' }),
  })
}

/** CI pre-index storage (Enterprise): bring-your-own-bucket. */
export function usePreindexSettings(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.preindexCfg(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/preindex`, { schema: preindexSettingsSchema }),
    enabled: Boolean(orgId),
  })
}

export function useSetPreindexSettings(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (settings: { bucket: string }) =>
      request(`/v1/organizations/${orgId}/preindex`, { method: 'PUT', body: settings }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.preindexCfg(orgId) })
    },
  })
}

/** Pre-auth SSO discovery for the sign-in page. Anonymous by necessity. */
export function discoverSSO(email: string) {
  return request(`/v1/auth/sso?email=${encodeURIComponent(email)}`, {
    schema: ssoDiscoverySchema,
    anonymous: true,
  })
}

const orgTokensResponse = z.object({ tokens: z.array(orgTokenSchema) })
const createdTokenResponse = z.object({ token: z.string(), info: orgTokenSchema })

export function useOrgTokens(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.orgTokens(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/tokens`, { schema: orgTokensResponse }),
    enabled: Boolean(orgId),
    select: (data) => data.tokens,
  })
}

/**
 * Mints a SCIM or CI credential. The secret in the response is shown exactly
 * once — it is stored hashed and can never be retrieved again.
 */
export function useCreateOrgToken(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (kind: 'scim' | 'ci') =>
      request(`/v1/organizations/${orgId}/tokens`, {
        method: 'POST',
        body: { kind },
        schema: createdTokenResponse,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.orgTokens(orgId) })
    },
  })
}

export function useRevokeOrgToken(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (kind: 'scim' | 'ci') =>
      request(`/v1/organizations/${orgId}/tokens/${kind}`, { method: 'DELETE' }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.orgTokens(orgId) })
    },
  })
}

export function useUpdateMember(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { memberId: string; role?: Role; status?: 'active' | 'suspended' }) =>
      request(`/v1/organizations/${orgId}/members/${input.memberId}`, {
        method: 'PATCH',
        body: { role: input.role ?? '', status: input.status ?? '' },
      }),
    onSuccess: () => invalidateWorkspace(client, orgId),
  })
}

export function useRemoveMember(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (memberId: string) =>
      request(`/v1/organizations/${orgId}/members/${memberId}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidateWorkspace(client, orgId)
      // Removing a member revokes their devices, so the workspace device list moved
      // too.
      void client.invalidateQueries({ queryKey: keys.orgDevices(orgId) })
    },
  })
}

export function useTransferOwnership(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    // The typed workspace name travels with the request: the backend refuses a
    // transfer without it, so collecting it is not just dialog theatre.
    mutationFn: (input: { memberId: string; confirmName: string }) =>
      request(`/v1/organizations/${orgId}/transfer-ownership`, {
        method: 'POST',
        body: { member_id: input.memberId, confirm_name: input.confirmName },
      }),
    onSuccess: () => {
      invalidateWorkspace(client, orgId)
      // The caller's own role changed, so their capability map is stale.
      void client.invalidateQueries({ queryKey: keys.account })
    },
  })
}

export function useRenameOrganization(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      request(`/v1/organizations/${orgId}`, { method: 'PATCH', body: { name } }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.org(orgId) })
      void client.invalidateQueries({ queryKey: keys.account })
    },
  })
}

const seatsResponse = z.object({ seats: z.array(seatSchema) })

export function useSeats(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.seats(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/seats`, { schema: seatsResponse }),
    enabled: Boolean(orgId),
    select: (data) => data.seats,
  })
}

export function useAssignSeat(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { seatId: string; memberId: string }) =>
      request(`/v1/organizations/${orgId}/seats/${input.seatId}/assign`, {
        method: 'POST',
        body: { member_id: input.memberId },
      }),
    onSuccess: () => invalidateWorkspace(client, orgId),
  })
}

export function useRevokeSeat(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (seatId: string) =>
      request(`/v1/organizations/${orgId}/seats/${seatId}/revoke`, { method: 'POST' }),
    onSuccess: () => invalidateWorkspace(client, orgId),
  })
}

export function useOrgDevices(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.orgDevices(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/devices`, { schema: devicesResponse }),
    enabled: Boolean(orgId),
    select: (data) => data.devices,
  })
}

export function useRevokeOrgDevice(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (deviceId: string) =>
      request(`/v1/organizations/${orgId}/devices/${deviceId}/revoke`, { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.orgDevices(orgId) }),
  })
}

const offlineLicensesResponse = z.object({ licenses: z.array(offlineLicenseSchema) })

export function useOfflineLicenses(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.offlineLicenses(orgId ?? ''),
    queryFn: () =>
      request(`/v1/organizations/${orgId}/offline-licenses`, { schema: offlineLicensesResponse }),
    enabled: Boolean(orgId),
    select: (data) => data.licenses,
  })
}

/**
 * The licence file is kept as whatever JSON the server sent and never
 * re-shaped: what the admin downloads has to be exactly what `chi auth import`
 * parses, and a schema here that dropped or renamed a field would hand out a
 * file that cannot be installed.
 */
const issuedOfflineLicenseResponse = z.object({
  license: z.record(z.unknown()),
  device: offlineLicenseSchema,
})

/**
 * Issues an offline licence for the machine that printed `request`, against
 * `memberId`'s seat. Issuing registers or finds that machine's device row, so
 * the workspace device list moves with it.
 */
export function useIssueOfflineLicense(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { memberId: string; request: string }) =>
      request(`/v1/organizations/${orgId}/offline-licenses`, {
        method: 'POST',
        body: { member_id: input.memberId, request: input.request },
        schema: issuedOfflineLicenseResponse,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.offlineLicenses(orgId) })
      void client.invalidateQueries({ queryKey: keys.orgDevices(orgId) })
    },
  })
}

// --- CAVS DB Server ---------------------------------------------------------------

const cavsdbLicensesResponse = z.object({ licenses: z.array(cavsdbLicenseSchema) })

/** Licences are admin-only; `enabled` lets the page skip the call for members. */
export function useCavsdbLicenses(orgId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.cavsdbLicenses(orgId ?? ''),
    queryFn: () =>
      request(`/v1/organizations/${orgId}/cavsdb/licenses`, { schema: cavsdbLicensesResponse }),
    enabled: Boolean(orgId) && enabled,
    select: (data) => data.licenses,
  })
}

/**
 * The newest server release on a channel. A 404 is the ordinary "nothing
 * published yet" answer, not a failure, so it is not retried.
 */
export function useCavsdbDownloads(orgId: string | undefined, channel = 'stable', enabled = true) {
  return useQuery({
    queryKey: keys.cavsdbDownloads(orgId ?? '', channel),
    queryFn: () =>
      request(`/v1/organizations/${orgId}/cavsdb/downloads?channel=${encodeURIComponent(channel)}`, {
        schema: cavsdbDownloadsSchema,
      }),
    enabled: Boolean(orgId) && enabled,
    retry: false,
  })
}

/** Generates a licence; any active one becomes superseded with a 7-day overlap. */
export function useIssueCavsdbLicense(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { label: string; closedNetwork: boolean }) =>
      request(`/v1/organizations/${orgId}/cavsdb/licenses`, {
        method: 'POST',
        body: { label: input.label, closed_network: input.closedNetwork },
        schema: cavsdbIssuedSchema,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.cavsdbLicenses(orgId) }),
  })
}

/** Re-signs an existing licence's token. Nothing is cached: it is a secret. */
export function useCavsdbLicenseToken(orgId: string) {
  return useMutation({
    mutationFn: (licenseId: string) =>
      request(`/v1/organizations/${orgId}/cavsdb/licenses/${licenseId}/token`, {
        schema: cavsdbTokenSchema,
      }),
  })
}

export function useRevokeCavsdbLicense(orgId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { licenseId: string; reason: string }) =>
      request(`/v1/organizations/${orgId}/cavsdb/licenses/${input.licenseId}/revoke`, {
        method: 'POST',
        body: { reason: input.reason },
        schema: z.object({ license: cavsdbLicenseSchema }),
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.cavsdbLicenses(orgId) }),
  })
}

export function usePolicies(orgId: string | undefined) {
  return useQuery({
    queryKey: keys.policies(orgId ?? ''),
    queryFn: () => request(`/v1/organizations/${orgId}/policies`, { schema: policiesViewSchema }),
    enabled: Boolean(orgId),
  })
}

/**
 * The SCIM group names the read-policy editor offers. Admins only.
 *
 * An API older than the route answers 404; that is "group rules unavailable",
 * not an error to show over the editor (#1302). Only the router's own 404, which
 * has no error envelope (code `unknown`): a `not_found` from the API is about the
 * workspace, and is shown as the error it is.
 */
export function usePolicyGroups(orgId: string | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: keys.policyGroups(orgId ?? ''),
    queryFn: () =>
      request(`/v1/organizations/${orgId}/policies/groups`, { schema: policyGroupsSchema }).catch(
        (error: unknown) => {
          if (error instanceof ApiError && error.isNotFound && error.code !== 'not_found') {
            return { available: false, groups: [] }
          }
          throw error
        },
      ),
    enabled: Boolean(orgId) && (options?.enabled ?? true),
  })
}

/**
 * The If-Match of a policy save: the version the form was drafted from (#1302).
 * None from an API that sends no versions, which then saves unconditionally.
 */
function ifMatch(version: string): Record<string, string> | undefined {
  return version ? { 'If-Match': `"${version}"` } : undefined
}

/** A save refused because somebody else saved first; the form offers a reload. */
export function isPoliciesChanged(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'policies_changed'
}

/**
 * Saves the workspace read policy alone. The other settings are never sent, so a
 * copy of them cached in this tab cannot overwrite a newer save. `{}` clears it.
 * Saved against `version`; a refusal refetches, so the panel sees their change.
 */
export function useSetReadPolicy(orgId: string) {
  const client = useQueryClient()
  const refresh = () => {
    void client.invalidateQueries({ queryKey: keys.policies(orgId) })
    void client.invalidateQueries({ queryKey: keys.org(orgId) })
  }
  return useMutation({
    mutationFn: ({ readPolicy, version }: { readPolicy: ReadPolicy; version: string }) =>
      request(`/v1/organizations/${orgId}/policies/read`, {
        method: 'PUT',
        body: { read_policy: readPolicy },
        headers: ifMatch(version),
      }),
    onSuccess: refresh,
    onError: (error) => {
      if (isPoliciesChanged(error)) refresh()
    },
  })
}

/** Saves the CLI settings against `version`, as useSetReadPolicy does. */
export function useSetPolicies(orgId: string) {
  const client = useQueryClient()
  const refresh = () => {
    void client.invalidateQueries({ queryKey: keys.policies(orgId) })
    void client.invalidateQueries({ queryKey: keys.org(orgId) })
  }
  return useMutation({
    mutationFn: ({ policies, version }: { policies: Policies; version: string }) =>
      request(`/v1/organizations/${orgId}/policies`, {
        method: 'PUT',
        body: policies,
        headers: ifMatch(version),
      }),
    onSuccess: refresh,
    onError: (error) => {
      if (isPoliciesChanged(error)) refresh()
    },
  })
}

const auditResponse = z.object({
  events: z.array(auditEventSchema),
  next_cursor: z.string().nullish(),
})

export function useAuditEvents(
  orgId: string | undefined,
  cursor: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: keys.audit(orgId ?? '', cursor),
    queryFn: () =>
      request(
        `/v1/organizations/${orgId}/audit-events?limit=50${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        { schema: auditResponse },
      ),
    enabled: Boolean(orgId) && (options?.enabled ?? true),
    // Keeping the previous page mounted while the next one loads avoids the list
    // collapsing to a spinner on every page change.
    placeholderData: (previous) => previous,
  })
}

export function useExport(orgId: string) {
  return useMutation({
    mutationFn: async (kind: 'members' | 'seats' | 'devices' | 'audit-events') => {
      const blob = await requestCsv(`/v1/organizations/${orgId}/exports/${kind}`)
      downloadBlob(blob, `archis-${kind}.csv`)
    },
  })
}

/**
 * The usage dashboard as a one-page CSV for the executive sponsor, priced at the
 * blended rate on screen so the file agrees with the page it was taken from.
 */
export function useUsageExport(orgId: string) {
  return useMutation({
    mutationFn: async (rate: number) => {
      const blob = await requestCsv(
        `/v1/organizations/${orgId}/exports/usage?rate=${encodeURIComponent(rate)}`,
      )
      downloadBlob(blob, 'archis-usage-roi.csv')
    },
  })
}
